// Hàng ảnh CHỜ gửi của ô soạn tin (CEO chốt 25/09/2026 — "2a"): chọn / dán ảnh
// thì ảnh vào hàng chờ chứ không bắn đi ngay, bấm Gửi mới đi. Dùng chung cho ô
// soạn của cổng khách, mục đối tác (ChatComposer) và ô soạn nhân viên
// (StaffComposer) — tách ra từ ChatComposer, không đổi hành vi.
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { PhotoThumbList, PhotoThumbRemove } from "@/components/PhotoThumb";
import { validateChatImage } from "../api";

/**
 * Trần số ảnh chờ gửi. Không phải giới hạn kỹ thuật — chặn cú lỡ tay chọn cả
 * thư mục 300 ảnh rồi treo máy khách suốt vài phút.
 */
export const MAX_IMAGES_PER_BATCH = 10;

/** Một ảnh đang chờ gửi. `url` là object URL, phải thu hồi khi bỏ đi. */
export type StagedImage = { file: File; url: string };

export function useStagedImages() {
  const [images, setImages] = useState<StagedImage[]>([]);

  // Thu hồi object URL khi RỜI MÀN — ảnh chờ mà không thu hồi là bộ nhớ treo
  // lại suốt phiên làm việc của nhân viên trực chat.
  //
  // Đọc qua ref, và mảng phụ thuộc của lượt dọn để RỖNG: để `[images]` thì mỗi
  // lần thêm ảnh React chạy dọn dẹp của lượt trước, thu hồi luôn URL của những
  // ảnh vẫn đang hiện — ô xem trước vỡ ngay từ ảnh thứ hai. Ref được cập nhật
  // trong effect chứ không phải giữa lúc vẽ (luật `react-hooks`).
  const imagesRef = useRef<StagedImage[]>([]);
  useEffect(() => {
    imagesRef.current = images;
  }, [images]);
  useEffect(
    () => () => {
      for (const img of imagesRef.current) URL.revokeObjectURL(img.url);
    },
    [],
  );

  const add = (list: File[]) => {
    if (list.length === 0) return;
    if (images.length + list.length > MAX_IMAGES_PER_BATCH) {
      toast.error(`Mỗi lần gửi tối đa ${MAX_IMAGES_PER_BATCH} ảnh.`);
      return;
    }
    // Kiểm HẾT trước khi nhận ảnh nào: nhận 3 ảnh rồi mới báo ảnh thứ 4 sai
    // định dạng là để người gửi ở trạng thái nửa vời, không biết phải làm gì.
    for (const file of list) {
      const problem = validateChatImage(file);
      if (problem) {
        toast.error(`${file.name}: ${problem}`);
        return;
      }
    }
    setImages((cur) => [...cur, ...list.map((file) => ({ file, url: URL.createObjectURL(file) }))]);
  };

  const remove = (url: string) => {
    URL.revokeObjectURL(url);
    setImages((cur) => cur.filter((img) => img.url !== url));
  };

  const clear = () => {
    for (const img of images) URL.revokeObjectURL(img.url);
    setImages([]);
  };

  return { images, add, remove, clear };
}

/** Dải ảnh đang chờ gửi, bỏ được từng cái trước khi bấm Gửi. */
export function StagedImagesStrip({
  images,
  onRemove,
  disabled,
}: {
  images: StagedImage[];
  onRemove: (url: string) => void;
  disabled: boolean;
}) {
  // Khung ảnh chuẩn 8x10, bấm xem lớn (CEO chốt 27/09/2026, QĐ 0224) — ô 64px cũ
  // không soát được bill / ảnh trước khi gửi khách. MỘT hàng vuốt ngang: chọn
  // nhiều ảnh cũng không đẩy ô gõ tin ra khỏi màn điện thoại.
  return (
    <div className="border-b bg-surface-2/50 px-3 py-2">
      <PhotoThumbList
        images={images.map((img) => img.url)}
        label="Ảnh đính kèm"
        openLabel={(i) => `Xem lớn ảnh đính kèm ${i + 1}`}
        className="flex-nowrap overflow-x-auto pb-1"
        itemClassName="bubble-staged-in shrink-0"
        overlay={(url, i) => (
          <PhotoThumbRemove label={`Bỏ ảnh ${i + 1}`} disabled={disabled} onClick={() => onRemove(url)} />
        )}
      />
    </div>
  );
}
